/** Currency list entry from GET /currencies */
export interface CurrencyDto {
  currency_code: string;
  currency_symbol: string;
  name_en: string;
  name_ar: string;
  name_he: string;
  is_active: boolean;
  decimal_places: number;
  sort_order: number;
}

export interface CurrenciesResponse {
  success: boolean;
  data: CurrencyDto[];
}

/** Exchange rate from GET /exchange-rates */
export interface ExchangeRateDto {
  currency_code: string;
  currency_symbol: string;
  base_currency_code: string;
  exchange_rate: number;
  exchange_rate_date: string;
  exchange_rate_source: string;
}

export interface ExchangeRateResponse {
  success: boolean;
  data: ExchangeRateDto;
}

/** GET /exchange-rates/current — latest BOI rates for all active foreign currencies */
export interface CurrentExchangeRatesData {
  base_currency_code: string;
  as_of: string;
  rates: ExchangeRateDto[];
}

export interface CurrentExchangeRatesResponse {
  success: boolean;
  data: CurrentExchangeRatesData;
}

export type ExchangeConvertDirection = 'FOREIGN_TO_ILS' | 'ILS_TO_FOREIGN';

export interface ExchangeConvertAmounts {
  subtotal?: number;
  discount?: number;
  vat?: number;
  total?: number;
}

export interface ExchangeConvertLineRequest {
  index: number;
  price_per_unit?: number;
  discount?: number;
  sub_total?: number;
  vat_amount?: number;
  total_amount?: number;
}

export interface ExchangeConvertRequest {
  currency_code: string;
  exchange_rate?: number;
  date?: string;
  direction: ExchangeConvertDirection;
  amounts?: ExchangeConvertAmounts;
  lines?: ExchangeConvertLineRequest[];
}

export interface ExchangeConvertAmountPair {
  subtotal?: number;
  discount?: number;
  vat?: number;
  total?: number;
}

export interface ExchangeConvertLinePair {
  index: number;
  foreign: {
    price_per_unit?: number;
    discount?: number;
    sub_total?: number;
    vat_amount?: number;
    total_amount?: number;
  };
  ils: {
    price_per_unit?: number;
    discount?: number;
    sub_total?: number;
    vat_amount?: number;
    total_amount?: number;
  };
}

export interface ExchangeConvertResponseData extends ExchangeRateDto {
  direction: ExchangeConvertDirection;
  amounts?: {
    foreign: ExchangeConvertAmountPair;
    ils: ExchangeConvertAmountPair;
  };
  lines?: ExchangeConvertLinePair[];
}

export interface ExchangeConvertResponse {
  success: boolean;
  data: ExchangeConvertResponseData;
}

/** Payload block for POST/PUT /invoices-v2 when selected document currency !== ILS */
export interface ForeignCurrencyPayload {
  currency_code: string;
  exchange_rate: number;
  exchange_rate_date: string;
  exchange_rate_source: string;
  exchange_rate_manually_changed?: boolean;
  subtotal_original: number;
  discount_original: number;
  vat_original: number;
  total_original: number;
}

/** foreignCurrency on GET invoice response (camelCase) */
export interface ForeignCurrencyEntity extends ForeignCurrencyPayload {
  id?: number;
  invoice_id?: number;
}

export const DEFAULT_CURRENCY_CODE = 'ILS';

/** Summary on list/filter rows — document display currency (not accounting `invoices.currency`). */
export interface CurrencySummary {
  currency_code: string;
  currency_symbol?: string;
  base_currency_code?: string;
  accounting_currency?: string;
  exchange_rate?: number;
  exchange_rate_date?: string;
  is_foreign?: boolean;
}
